import { useEffect, useMemo, useState } from "react"
import { apiClient } from "@/lib/api"
import { useToast } from "@stayframe/ui"

type ConfigType = "places" | "house-rules"
type CopyStrategy = "merge" | "replace"
type Step = 1 | 2 | 3

interface PropertyOption {
  id: string
  name: string
}

interface CopyTargetPreview {
  property_id: string
  property_name: string
  will_change: boolean
  before_count: number
  after_count: number
  added_count: number
  removed_count: number
  unchanged_count: number
  updated_count: number
  changed_sections: number
  unchanged_sections: number
}

interface CopyPreview {
  previewToken: string
  expiresAt: string
  targets: CopyTargetPreview[]
}

interface CopyConfigModalProps {
  isOpen: boolean
  sourcePropertyId: string
  sourcePropertyName: string
  configType: ConfigType
  properties: PropertyOption[]
  onClose: () => void
  onApplied?: () => void
}

function readString(value: unknown): string {
  return typeof value === "string" ? value : ""
}

function readNumber(value: unknown): number {
  const n = Number(value)
  return Number.isFinite(n) ? n : 0
}

function readBoolean(value: unknown): boolean {
  return value === true
}

function parsePreviewTargets(rawTargets: unknown): CopyTargetPreview[] {
  if (!Array.isArray(rawTargets)) return []
  const parsed: CopyTargetPreview[] = []
  for (const item of rawTargets) {
    if (!item || typeof item !== "object") continue
    const row = item as Record<string, unknown>
    const propertyId = readString(row.property_id)
    if (!propertyId) continue
    parsed.push({
      property_id: propertyId,
      property_name: readString(row.property_name) || propertyId,
      will_change: readBoolean(row.will_change),
      before_count: readNumber(row.before_count),
      after_count: readNumber(row.after_count),
      added_count: readNumber(row.added_count),
      removed_count: readNumber(row.removed_count),
      unchanged_count: readNumber(row.unchanged_count),
      updated_count: readNumber(row.updated_count),
      changed_sections: readNumber(row.changed_sections),
      unchanged_sections: readNumber(row.unchanged_sections),
    })
  }
  return parsed
}

function parseApplyCounts(targets: unknown): { applied: number; noChange: number } {
  if (!Array.isArray(targets)) return { applied: 0, noChange: 0 }
  let applied = 0
  let noChange = 0
  for (const item of targets) {
    if (!item || typeof item !== "object") continue
    const status = readString((item as Record<string, unknown>).status)
    if (status === "applied") applied += 1
    if (status === "no_change") noChange += 1
  }
  return { applied, noChange }
}

export function CopyConfigModal({
  isOpen,
  sourcePropertyId,
  sourcePropertyName,
  configType,
  properties,
  onClose,
  onApplied,
}: CopyConfigModalProps) {
  const { toast } = useToast()
  const [step, setStep] = useState<Step>(1)
  const [selectedTargetIds, setSelectedTargetIds] = useState<string[]>([])
  const [strategy, setStrategy] = useState<CopyStrategy>(configType === "places" ? "merge" : "replace")
  const [previewLoading, setPreviewLoading] = useState(false)
  const [applyLoading, setApplyLoading] = useState(false)
  const [error, setError] = useState("")
  const [preview, setPreview] = useState<CopyPreview | null>(null)
  const [requiresPreviewRefresh, setRequiresPreviewRefresh] = useState(false)

  const eligibleTargets = useMemo(() => {
    return properties
      .filter((property) => property.id !== sourcePropertyId)
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [properties, sourcePropertyId])

  const configLabel = configType === "places" ? "Places" : "House Rules"
  const strategyValue: CopyStrategy = configType === "places" ? strategy : "replace"
  const busy = previewLoading || applyLoading

  useEffect(() => {
    if (!isOpen) return
    setStep(1)
    setSelectedTargetIds([])
    setStrategy(configType === "places" ? "merge" : "replace")
    setPreview(null)
    setError("")
    setPreviewLoading(false)
    setApplyLoading(false)
    setRequiresPreviewRefresh(false)
  }, [isOpen, configType, sourcePropertyId])

  useEffect(() => {
    if (!isOpen) return
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape" && !busy) {
        onClose()
      }
    }
    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [isOpen, onClose, busy])

  if (!isOpen) return null

  function toggleTarget(targetId: string) {
    setError("")
    setPreview(null)
    setRequiresPreviewRefresh(false)
    setSelectedTargetIds((prev) => {
      if (prev.includes(targetId)) {
        return prev.filter((id) => id !== targetId)
      }
      return [...prev, targetId]
    })
  }

  async function runPreview() {
    if (!sourcePropertyId) return
    if (!selectedTargetIds.length) {
      setError("Select at least one target property.")
      return
    }

    setPreviewLoading(true)
    setApplyLoading(false)
    setError("")
    setRequiresPreviewRefresh(false)

    const orderedTargets = [...selectedTargetIds]

    const result = await apiClient.previewConfigCopy(
      {
        config_type: configType,
        target_property_ids: orderedTargets,
        strategy: strategyValue,
      },
      sourcePropertyId,
    )

    if (!result.ok) {
      setPreview(null)
      setError(result.error || `Failed to preview ${configLabel.toLowerCase()} copy.`)
      setPreviewLoading(false)
      return
    }

    const previewToken = readString(result.preview_token)
    if (!previewToken) {
      setPreview(null)
      setError("Preview did not return a preview token. Please try again.")
      setPreviewLoading(false)
      return
    }

    const targets = parsePreviewTargets(result.targets)
    setPreview({
      previewToken,
      expiresAt: readString(result.preview_expires_at),
      targets,
    })
    setStep(3)
    setPreviewLoading(false)
  }

  async function applyChanges() {
    if (!sourcePropertyId || !preview?.previewToken) return
    setApplyLoading(true)
    setError("")

    const result = await apiClient.applyConfigCopy(
      {
        config_type: configType,
        target_property_ids: [...selectedTargetIds],
        strategy: strategyValue,
        preview_token: preview.previewToken,
      },
      sourcePropertyId,
    )

    if (!result.ok) {
      const code = readString(result.code)
      if (code === "preview_expired" || code === "preview_stale" || code === "preview_mismatch" || code === "invalid_preview_token") {
        setRequiresPreviewRefresh(true)
      }
      setError(result.error || `Failed to apply ${configLabel.toLowerCase()} copy.`)
      setApplyLoading(false)
      return
    }

    const counts = parseApplyCounts(result.targets)
    if (counts.applied > 0) {
      toast(`${configLabel} copied to ${counts.applied} propert${counts.applied === 1 ? "y" : "ies"}.`, "success")
    } else {
      toast(`No ${configLabel.toLowerCase()} changes were needed on target properties.`, "info")
    }

    if (counts.noChange > 0 && counts.applied > 0) {
      toast(`${counts.noChange} propert${counts.noChange === 1 ? "y was" : "ies were"} unchanged.`, "info")
    }

    onApplied?.()
    setApplyLoading(false)
    onClose()
  }

  const canProceedStep1 = selectedTargetIds.length > 0

  function renderStepTargets() {
    if (!eligibleTargets.length) {
      return (
        <div className="copy-config-empty">
          Add another property first. Copy requires at least one target property.
        </div>
      )
    }

    return (
      <>
        <p className="copy-config-step-copy">
          Choose target properties.
        </p>
        <div className="copy-config-target-list" role="list">
          {eligibleTargets.map((property) => {
            const checked = selectedTargetIds.includes(property.id)
            return (
              <label key={property.id} className="copy-config-target" role="listitem">
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggleTarget(property.id)}
                  disabled={previewLoading || applyLoading}
                />
                <span className="copy-config-target__name">{property.name}</span>
              </label>
            )
          })}
        </div>
      </>
    )
  }

  function renderStepStrategy() {
    if (configType !== "places") {
      return (
        <>
          <p className="copy-config-step-copy">
            House rules copy uses a fixed strategy.
          </p>
          <div className="copy-config-strategy-fixed">
            House Rules supports <strong>Replace only</strong>. Existing target rules are replaced in full.
          </div>
        </>
      )
    }

    return (
      <>
        <p className="copy-config-step-copy">
          Choose how existing places in target properties should be handled.
        </p>
        <div className="copy-config-strategy-options">
          <label className={`copy-config-strategy${strategy === "merge" ? " copy-config-strategy--active" : ""}`}>
            <input
              type="radio"
              name="copy-strategy"
              value="merge"
              checked={strategy === "merge"}
              onChange={() => setStrategy("merge")}
            />
            <div>
              <div className="copy-config-strategy__title">Merge (recommended)</div>
              <div className="copy-config-strategy__desc">Keep existing places and add only missing source places.</div>
            </div>
          </label>
          <label className={`copy-config-strategy${strategy === "replace" ? " copy-config-strategy--active" : ""}`}>
            <input
              type="radio"
              name="copy-strategy"
              value="replace"
              checked={strategy === "replace"}
              onChange={() => setStrategy("replace")}
            />
            <div>
              <div className="copy-config-strategy__title">Replace</div>
              <div className="copy-config-strategy__desc">Overwrite each target with the source set.</div>
            </div>
          </label>
        </div>
      </>
    )
  }

  function renderStepPreview() {
    if (previewLoading) {
      return (
        <div className="copy-config-loading">
          <span className="sf-spinner" />
          Generating preview...
        </div>
      )
    }

    if (!preview) {
      return (
        <div className="copy-config-empty">
          Preview is not ready yet. Go back and run preview.
        </div>
      )
    }

    if (!preview.targets.length) {
      return (
        <div className="copy-config-empty">
          No preview data returned.
        </div>
      )
    }

    return (
      <>
        <p className="copy-config-step-copy">
          Review impact by property before applying.
        </p>
        <div className="copy-config-preview-list">
          {preview.targets.map((target) => (
            <div key={target.property_id} className="copy-config-preview-row">
              <div className="copy-config-preview-row__header">
                <span className="copy-config-preview-row__name">{target.property_name}</span>
                <span className={`copy-config-preview-row__badge${target.will_change ? " copy-config-preview-row__badge--change" : ""}`}>
                  {target.will_change ? "Will change" : "No change"}
                </span>
              </div>
              <div className="copy-config-preview-row__sub">
                {configType === "places"
                  ? `${target.before_count} -> ${target.after_count} places`
                  : `${target.before_count} -> ${target.after_count} sections`}
              </div>

              {configType === "places" ? (
                <div className="copy-config-metrics">
                  <span className="copy-config-metric-chip copy-config-metric-chip--add">+{target.added_count} add</span>
                  <span className="copy-config-metric-chip copy-config-metric-chip--remove">-{target.removed_count} remove</span>
                  <span className="copy-config-metric-chip">~{target.updated_count} update</span>
                  <span className="copy-config-metric-chip">={target.unchanged_count} same</span>
                </div>
              ) : (
                <div className="copy-config-metrics">
                  <span className="copy-config-metric-chip">{target.changed_sections} changed</span>
                  <span className="copy-config-metric-chip">{target.unchanged_sections} same</span>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="copy-config-warning">
          This cannot be undone.
          {preview.expiresAt ? (
            <span className="copy-config-warning__meta"> Preview expires at {new Date(preview.expiresAt).toLocaleTimeString()}.</span>
          ) : null}
        </div>
      </>
    )
  }

  return (
    <div className="copy-config-overlay" onClick={() => !busy && onClose()}>
      <div className="copy-config-modal" onClick={(event) => event.stopPropagation()} role="dialog" aria-modal="true" aria-label={`Copy ${configLabel}`}>
        <div className="copy-config-modal__header">
          <div>
            <h3 className="copy-config-modal__title">Copy {configLabel}</h3>
            <p className="copy-config-modal__subtitle">
              Source: <strong>{sourcePropertyName || "Current property"}</strong>
            </p>
          </div>
          <button
            type="button"
            className="copy-config-modal__close"
            onClick={onClose}
            disabled={busy}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="copy-config-steps" aria-hidden>
          <span className={`copy-config-step${step === 1 ? " copy-config-step--active" : ""}`}>1 Targets</span>
          <span className={`copy-config-step${step === 2 ? " copy-config-step--active" : ""}`}>2 Strategy</span>
          <span className={`copy-config-step${step === 3 ? " copy-config-step--active" : ""}`}>3 Preview</span>
        </div>

        <div className="copy-config-modal__body">
          {step === 1 && renderStepTargets()}
          {step === 2 && renderStepStrategy()}
          {step === 3 && renderStepPreview()}

          {error && (
            <div className="copy-config-error" role="alert">
              {error}
            </div>
          )}
        </div>

        <div className="copy-config-modal__actions">
          {step === 1 && (
            <>
              <button className="sf-btn sf-btn--ghost" type="button" onClick={onClose} disabled={busy}>
                Cancel
              </button>
              <button
                className="sf-btn sf-btn--primary"
                type="button"
                onClick={() => setStep(2)}
                disabled={!canProceedStep1 || busy}
              >
                Next: Strategy
              </button>
            </>
          )}

          {step === 2 && (
            <>
              <button className="sf-btn sf-btn--ghost" type="button" onClick={() => setStep(1)} disabled={busy}>
                Back
              </button>
              <button className="sf-btn sf-btn--primary" type="button" onClick={runPreview} disabled={busy}>
                {previewLoading ? "Loading..." : "Preview changes"}
              </button>
            </>
          )}

          {step === 3 && (
            <>
              <button className="sf-btn sf-btn--ghost" type="button" onClick={() => setStep(2)} disabled={busy}>
                Back
              </button>
              {requiresPreviewRefresh && (
                <button className="sf-btn sf-btn--secondary" type="button" onClick={runPreview} disabled={busy}>
                  Refresh preview
                </button>
              )}
              <button className="sf-btn sf-btn--primary" type="button" onClick={applyChanges} disabled={busy || !preview || requiresPreviewRefresh}>
                {applyLoading ? "Applying..." : "Apply copy"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
